import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import logo from './asset/logo.png';
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Navbar({ activeSection, setCurrentPage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ Scroll only on home page
  const scrollToSection = (section) => {
    if (location.pathname === "/") {
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate("/"); // send user to home first
      setTimeout(() => {
        const el = document.getElementById(section);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrollY > 50 ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo → always go home */}
          <Link to="/" className="flex items-center gap-3">
            <div className="relative">
              <img src={logo} alt="Description" width="100px" />
              <div className="absolute inset-0 bg-blue-500/30 blur-xl rounded-full" />
            </div>
            <span className="text-white font-bold text-2xl bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
              FreeIeee
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Sections (scroll on home, navigate otherwise) */}
            {['home', 'features', 'benefits', 'pricing', 'testimonials', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`text-sm font-medium transition-all duration-300 ${
                  activeSection === item 
                    ? 'text-white scale-110' 
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}

            {/* Careers → always a route */}
            <Link 
  to="/carrier" 
  className={`text-sm font-medium ${
    location.pathname === "/carrier" 
      ? "text-white scale-110" 
      : "text-white/60 hover:text-white"
  }`}
>
  Careers
</Link>


            <button
              onClick={() => setCurrentPage('login')}
              className="text-white/90 hover:text-white transition text-sm font-medium"
            >
              Login
            </button>

            <button
              onClick={() => setCurrentPage('signup')}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition" />
              <div className="relative bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm">
                Get Started
              </div>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden text-white">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden pb-6 space-y-4 animate-fadeIn">
            {['home', 'features', 'benefits', 'pricing', 'testimonials', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => { scrollToSection(item); setMenuOpen(false); }}
                className="block w-full text-left text-white/90 hover:text-white py-2 text-sm font-medium"
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}

            <Link 
  to="/carrier" 
  onClick={() => setMenuOpen(false)} 
  className="block w-full text-left text-white/90 hover:text-white py-2 text-sm font-medium"
>
  Careers
</Link>


            <button
              onClick={() => { setCurrentPage('login'); setMenuOpen(false); }}
              className="block w-full text-left text-white/90 hover:text-white py-2 text-sm font-medium"
            >
              Login
            </button>

            <button
              onClick={() => { setCurrentPage('signup'); setMenuOpen(false); }}
              className="block w-full bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-6 py-2 rounded-full font-semibold text-sm"
            >
              Get Started
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
